import { PixelPet } from '../identity/PixelPet'
import { markKeywords } from '../ui/markKeywords'
import { relativeAge } from '../ui/relativeTime'
import type { AccountSearchMachine, AccountSearchReport, AccountSearchResult } from '../transport/relay'

/**
 * THE MESSAGES A GLOBAL SEARCH FOUND — owner, 2026-09-28: 改造 side panel 右下角的這一個
 * 搜尋按鈕, 讓使用者可以按下這個搜尋按鈕, 就可以全域的搜尋他所有機器裡面所有的訊息.
 *
 * One row per message, best first (the machines' own ranking, merged by the BFF): whose
 * agent said it and on which machine, how long ago, and the words around the keyword
 * with the keyword marked. A tap opens that agent at that message. Under the rows, every
 * machine that could NOT be searched says why — a machine that was offline or is too old
 * for search must not read as "no messages there".
 *
 * THE WAIT IS SHOWN, AND THE ANSWER ARRIVES — owner, 2026-09-29: 從後端開始搜尋, 傳遞訊息
 * 到前端有一定的延遲 … 我們可以使用一個過場動畫讓整個 side panel 呈現一個忙碌在搜尋的狀態,
 * 然後呢, 等資料收集到之後, 再一一的呈現在 side panel 上面. This is the whole list while a
 * search is up (Sidebar's `only`), so the placeholder rows fill the panel while every
 * machine reads its conversations, and the results come in one after another. Motion
 * is the stylesheet's (msg-*), and off for anyone who asked their system for less.
 *
 * No ✕ of its own: the one in the search bar clears the search and brings the panel
 * back, and a second one here would be a second way to say the same thing.
 */

/** Placeholder rows while every machine is searched — enough to fill a phone's panel. */
const PLACEHOLDERS = 12
/** Rows after this one arrive together: a long answer should not take seconds to finish drawing. */
const STAGGERED = 12

const STATUS_WORDS: Record<AccountSearchMachine['status'], string> = {
  ok: 'searched',
  offline: 'offline — not searched',
  needs_update: 'needs an update to be searched',
  not_here: 'does not have that agent',
  busy: 'busy — try again in a moment',
  timeout: 'did not answer in time',
  error: 'could not search',
}

/**
 * Who sent a message that arrived: the agent by its roster name when it is one of the
 * account's, else the name its frame carried, else what kind of sender it was. A `user`
 * block is either the owner's typing or an arrival, and only the first is "you".
 */
function senderOf(from: NonNullable<AccountSearchResult['from']>, nameOf?: (hash: string) => string | null): string {
  const known = from.hash ? nameOf?.(from.hash) : null
  return known || from.label || (from.kind === 'agent' ? 'another agent' : from.kind === 'chat' ? 'a chat' : 'a project')
}

export function MessageResults({
  query,
  keywords,
  pending,
  error,
  report,
  now,
  onOpen,
  nameOf,
}: {
  query: string
  keywords: readonly string[]
  pending: boolean
  error: string | null
  report: AccountSearchReport | undefined
  /** Epoch seconds, so a test can pin "how long ago". */
  now: number
  onOpen: (r: AccountSearchResult) => void
  /** An agent's session name for its channel hash, from the account's roster. */
  nameOf?: (hash: string) => string | null
}) {
  const notSearched = (report?.machines ?? []).filter((m) => m.status !== 'ok')
  return (
    <div className="sb-sec msg-results" role="region" aria-label={`Messages matching ${query}`}>
      <div className="sb-sec-hdrow">
        <div className="sb-sec-hd msg-results-hd">
          <span className="sb-sec-label">Messages</span>
          {report && !pending && <span className="sb-sec-count">· {report.total_matches}</span>}
        </div>
      </div>
      <div className="sb-sec-body">
        {pending && (
          <div className="msg-busy" role="status" aria-live="polite">
            <div className="msg-busy-bar" aria-hidden="true" />
            <div className="msg-busy-line">
              <span className="msg-busy-dot" aria-hidden="true" />
              Searching every machine for “{query}”…
            </div>
            {Array.from({ length: PLACEHOLDERS }, (_, i) => (
              <div key={i} className="msg-skel" aria-hidden="true" style={{ ['--i' as string]: i }}>
                <span className="msg-skel-face" />
                <span className="msg-skel-lines">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            ))}
          </div>
        )}
        {!pending && error && <div className="sb-empty sb-err">Search failed: {error}</div>}
        {!pending && report && report.results.length === 0 && (
          <div className="sb-empty">No messages match “{query}”.</div>
        )}
        {!pending &&
          report?.results.map((r, i) => (
            <button
              type="button"
              key={`${r.device_id}/${r.hash}/${r.index}`}
              className="msg-hit"
              style={{ ['--i' as string]: Math.min(i, STAGGERED) }}
              onClick={() => onOpen(r)}
              title={`Open ${r.session} at this message`}
            >
              <span className="msg-hit-face" aria-hidden="true">
                <PixelPet name={r.session} seed={r.hash} size={20} />
              </span>
              <span className="msg-hit-body">
                <span className="msg-hit-top">
                  <span className="msg-hit-who">{r.session}</span>
                  <span className="msg-hit-where">@ {r.machine}</span>
                  {r.from ? (
                    <span className="msg-hit-kind">from {senderOf(r.from, nameOf)}</span>
                  ) : (
                    r.kind === 'user' && <span className="msg-hit-kind">you</span>
                  )}
                  {r.ts !== undefined && (
                    <span className="msg-hit-when">
                      {r.ts_estimated ? '~' : ''}
                      {relativeAge(now - r.ts)}
                    </span>
                  )}
                </span>
                <span className="msg-hit-text">{markKeywords(r.text, keywords)}</span>
              </span>
            </button>
          ))}
        {!pending && report && (
          <div className="msg-results-foot">
            Searched {report.machines_searched} of {report.machines_total} machine
            {report.machines_total === 1 ? '' : 's'}
            {notSearched.map((m) => (
              <div key={m.device_id} className="msg-results-miss">
                {m.machine}: {STATUS_WORDS[m.status]}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
